import { Suspense } from "react";
import { Await, useLoaderData } from "react-router-dom";
import { ClimbingBoxLoader } from "react-spinners";
import { Workout } from "../concept2";
import { secondsAsPace } from "../vegahelpers";

interface WorkoutBest {
    comments: string;
    count: number;
    bestPace: number;
    bestDate: string;
}

function computePersonalBests(workouts: Workout[]): WorkoutBest[] {
    const byComment = new Map<string, { count: number; bestPace: number; bestDate: string }>();

    for (const workout of workouts) {
        const name = workout.comments;
        if (!name || name === "None" || workout.distance <= 0 || workout.time <= 0) continue;

        const pace = workout.time * 50 / workout.distance;
        if (!isFinite(pace) || pace <= 0) continue;

        const existing = byComment.get(name);
        if (!existing) {
            byComment.set(name, { count: 1, bestPace: pace, bestDate: workout.date });
        } else {
            existing.count++;
            if (pace < existing.bestPace) {
                existing.bestPace = pace;
                existing.bestDate = workout.date;
            }
        }
    }

    return Array.from(byComment.entries())
        .map(([comments, v]) => ({ comments, ...v }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
}

function PersonalBestsTable({ workouts }: { workouts: Workout[] }) {
    const bests = computePersonalBests(workouts);

    return (
        <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '1.1rem' }}>
            <thead>
                <tr style={{ borderBottom: '2px solid #574f6e', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 1rem' }}>Workout</th>
                    <th style={{ padding: '0.5rem 1rem' }}>Times Done</th>
                    <th style={{ padding: '0.5rem 1rem' }}>Best Pace</th>
                    <th style={{ padding: '0.5rem 1rem' }}>Best Date</th>
                </tr>
            </thead>
            <tbody>
                {bests.map((row, i) => (
                    <tr key={row.comments} style={{ borderBottom: '1px solid #e0e0e0', background: i % 2 === 0 ? '#fafafa' : 'white' }}>
                        <td style={{ padding: '0.5rem 1rem' }}>{row.comments}</td>
                        <td style={{ padding: '0.5rem 1rem' }}>{row.count}</td>
                        <td style={{ padding: '0.5rem 1rem' }}><b>{secondsAsPace(row.bestPace)}</b></td>
                        <td style={{ padding: '0.5rem 1rem' }}>{new Date(row.bestDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    </tr>
                ))}
            </tbody>
        </table>
        </div>
    );
}

export default function PersonalBests() {
    const loaderData: any = useLoaderData();

    return (
        <div>
            <h1>Personal Bests</h1>
            <Suspense fallback={<div><h1>Loading workout data...</h1><ClimbingBoxLoader color="#574f6e" /></div>}>
                <Await resolve={loaderData.data}>
                    {(workouts: Workout[]) => <PersonalBestsTable workouts={workouts} />}
                </Await>
            </Suspense>
        </div>
    );
}
